/**
 * @fileoverview NarrativeWarningNote — the soft narrative prompt shown under a
 * time entry's narrative field (MOR-1039).
 *
 * A small amber badge naming the issue ("Generic", "Brief for 2 hours"), with
 * the full explanation on hover, so a flagged row stays one line tall and the
 * reason is a glance away. Renders nothing when the narrative is fine. Never
 * blocks a save: it is a nudge to add detail before the entry goes for
 * approval.
 *
 * Firm-gated: when given an `orgId` it reads the firm's timesheet settings and
 * shows nothing unless narrative flagging is switched on, and it appends the
 * firm's own guidance (their ARITA references and policies) to the message.
 * Without an `orgId` — outside an org context, e.g. unit tests — it falls back
 * to the built-in prompt so the pure check can be exercised directly.
 *
 * `units` is whatever the units box currently holds, part units included. It
 * is rounded here in the firm's rounding direction, the same way the save
 * rounds it, so the prompt judges the entry that will actually be posted.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";
import { roundUnits } from "@cvx/timesheets/units";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { checkNarrative } from "./narrativeCheck";

interface BaseProps {
  narrative: string;
  units: number;
  unitMinutes: number;
  /**
   * The org whose narrative settings gate this prompt. Omit only where there
   * is no org context (unit tests), in which case the built-in prompt shows.
   */
  orgId?: Id<"organizations">;
  className?: string;
}

export function NarrativeWarningNote({ orgId, ...rest }: BaseProps) {
  if (orgId) return <ConfiguredNarrativeWarning orgId={orgId} {...rest} />;
  return <NarrativeWarningBadge {...rest} referenceText={undefined} />;
}

/**
 * Reads the firm's settings and only prompts when flagging is on, appending
 * the firm's own guidance to the message.
 */
function ConfiguredNarrativeWarning({
  orgId,
  ...rest
}: Omit<BaseProps, "orgId"> & { orgId: Id<"organizations"> }) {
  const { data: settings } = useQuery(
    convexQuery(api.timesheets.settings.getForOrg, { orgId }),
  );
  if (!settings?.narrativeFlaggingEnabled) return null;
  return (
    <NarrativeWarningBadge
      {...rest}
      referenceText={settings.narrativeContext}
      roundingMode={settings.roundingMode}
    />
  );
}

function NarrativeWarningBadge({
  narrative,
  units,
  unitMinutes,
  referenceText,
  roundingMode,
  className,
}: Omit<BaseProps, "orgId"> & {
  referenceText: string | undefined;
  roundingMode?: TimeRoundingMode;
}) {
  const warning = checkNarrative(
    narrative,
    roundUnits(units, roundingMode),
    unitMinutes,
    referenceText,
  );
  if (!warning) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={warning.message}
            className={cn("inline-flex", className)}
          >
            <Badge color="red" variant="soft" size="sm" className="cursor-help">
              {warning.label}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <p>{warning.message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
